import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { festivalsService } from '../../services';

const emptyForm = {
  name: '',
  sanskrit_name: '',
  description: '',
  significance: '',
  start_date: '',
  end_date: '',
  lunar_calendar: '',
  duration_days: 1,
  ranking: 0,
  popularity_score: 0,
  participating_temples: '',
  images: '',
};

const FestivalManagement = () => {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadFestivals();
  }, []);

  const loadFestivals = async () => {
    try {
      setLoading(true);
      const data = await festivalsService.getAllFestivals();
      setFestivals(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load festivals.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (festival = null) => {
    if (festival) {
      setEditId(festival.id);
      setForm({
        name: festival.name || '',
        sanskrit_name: festival.sanskrit_name || '',
        description: festival.description || '',
        significance: festival.significance || '',
        start_date: festival.dates?.start_date || '',
        end_date: festival.dates?.end_date || '',
        lunar_calendar: festival.dates?.lunar_calendar || '',
        duration_days: festival.dates?.duration_days || 1,
        ranking: festival.ranking || 0,
        popularity_score: festival.popularity_score || 0,
        participating_temples: (festival.participating_temples || []).join(', '),
        images: (festival.images || []).join(', '),
      });
    } else {
      setEditId(null);
      setForm(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm(emptyForm);
  };

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const toPayload = () => ({
    name: form.name,
    sanskrit_name: form.sanskrit_name || undefined,
    description: form.description,
    significance: form.significance || undefined,
    dates: {
      start_date: form.start_date,
      end_date: form.end_date,
      lunar_calendar: form.lunar_calendar,
      duration_days: Number(form.duration_days) || 1,
    },
    ranking: Number(form.ranking) || 0,
    popularity_score: Number(form.popularity_score) || 0,
    participating_temples: form.participating_temples
      ? form.participating_temples.split(',').map((s) => s.trim()).filter(Boolean)
      : [],
    images: form.images
      ? form.images.split(',').map((s) => s.trim()).filter(Boolean)
      : [],
  });

  const handleSubmit = async () => {
    try {
      const payload = toPayload();
      if (editId) {
        await festivalsService.updateFestival(editId, payload);
        setSuccess('Festival updated successfully.');
      } else {
        await festivalsService.createFestival(payload);
        setSuccess('Festival created successfully.');
      }
      handleClose();
      loadFestivals();
    } catch (err) {
      setError('Failed to save festival.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await festivalsService.deleteFestival(id);
      setSuccess('Festival deleted successfully.');
      loadFestivals();
    } catch (err) {
      setError('Failed to delete festival.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Festival Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Festival
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Ranking</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {festivals.length > 0 ? (
                festivals.map((festival) => (
                  <TableRow key={festival.id}>
                    <TableCell>
                      {festival.name}
                      {festival.sanskrit_name && (
                        <Chip label={festival.sanskrit_name} size="small" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{festival.dates?.start_date || '—'}</TableCell>
                    <TableCell>{festival.dates?.duration_days || '—'} days</TableCell>
                    <TableCell>{festival.ranking}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(festival)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(festival.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No festivals yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Festival' : 'Add Festival'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                required
                value={form.name}
                onChange={handleChange('name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sanskrit Name"
                fullWidth
                value={form.sanskrit_name}
                onChange={handleChange('sanskrit_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                required
                multiline
                rows={2}
                value={form.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Significance"
                fullWidth
                multiline
                rows={2}
                value={form.significance}
                onChange={handleChange('significance')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.start_date}
                onChange={handleChange('start_date')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.end_date}
                onChange={handleChange('end_date')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Duration (days)"
                type="number"
                fullWidth
                value={form.duration_days}
                onChange={handleChange('duration_days')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Lunar Calendar"
                fullWidth
                value={form.lunar_calendar}
                onChange={handleChange('lunar_calendar')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ranking"
                type="number"
                fullWidth
                value={form.ranking}
                onChange={handleChange('ranking')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Popularity Score"
                type="number"
                fullWidth
                value={form.popularity_score}
                onChange={handleChange('popularity_score')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Participating Temples (comma separated)"
                fullWidth
                value={form.participating_temples}
                onChange={handleChange('participating_temples')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Image URLs (comma separated)"
                fullWidth
                value={form.images}
                onChange={handleChange('images')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.name || !form.description}>
            {editId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FestivalManagement;
